/*
 * FTP Bundle Upsell Modal
 * -----------------------
 * Native (no-app) replacement for Kaching Bundles' post-add-to-cart popup.
 * Flow: add to cart -> this modal shows (tier stepper + qty stepper + cross-sell "STACK AND SAVE")
 *       -> "Go to Cart" opens the real cart drawer.
 *
 * Tier logic mirrors the 2 native Shopify discounts configured for this store (both scoped to the
 * "Bundle Eligible - Mix & Match" collection, which includes ALL bundle-relevant products):
 *   - 2+ items: 10% off the whole order.
 *   - 3+ items: 20% off the whole order.
 * Both tiers must stay the SAME discount type (Basic order discount) -- mixing types (e.g. BXGY vs
 * Basic) breaks Shopify's tier comparison silently (confirmed the hard way in an earlier version).
 *
 * window.__ftpModalActive / body[data-ftp-suppress-cart] is a global suppression flag+attribute: while
 * set, window.kachingCartApi.openCart() is wrapped to no-op AND a CSS rule force-hides Kaching Cart's own
 * drawer (#kaching-cart) regardless of which internal code path it uses to show itself. The cart the
 * shopper actually sees on this store is NOT the theme's <cart-drawer> -- it's the separate Kaching Cart
 * app's own drawer, driven by window.kachingCartApi.{openCart,closeCart,refreshCart}. Suppression is set
 * as early as possible (capture-phase submit listener on any /cart/add form) to avoid a flicker of the
 * real drawer before this modal appears. Only goToCart() clears it, and it opens Kaching's drawer.
 *
 * connectedCallback() reparents this element to be a direct child of <body>. It was originally rendered
 * inline in theme.liquid, nested inside a container that turned out to establish a CSS containing block
 * for position:fixed descendants (some ancestor has a transform/similar) -- that silently shrank this
 * modal to a small region instead of the full viewport, which meant the dark overlay never actually
 * covered the page and, worse, the page underneath stayed scrollable/interactive (a sticky add-to-cart
 * bar could bleed through). Moving to <body> guarantees position:fixed is relative to the real viewport.
 *
 * PERF (2026-09-15): show() previously made 4 SEQUENTIAL network round trips before the modal finished
 * painting -- fetchCart() for baseQty, then refreshModalTier()'s own independent fetchCart(), then
 * refreshDrawerBanner()'s own independent fetchCart() (3 identical /cart.js calls in a row for data that
 * doesn't change between them), then renderCrossSell()'s recommendations fetch last. That is exactly why
 * the modal visibly rendered top-to-bottom in stages instead of appearing as one clean paint. Fixed by
 * (a) fetching the cart ONCE and passing it into refreshModalTier/refreshDrawerBanner instead of each
 * re-fetching, and (b) kicking off renderCrossSell's fetch immediately (it only needs productId, known
 * synchronously) instead of waiting for the cart/quantity work to finish first, then running the
 * remaining independent renders via Promise.all instead of sequential awaits.
 *
 * SCROLLBAR (2026-09-16): the dialog's own scrollbar is styled transparent by default (see CSS) and
 * only tinted visible while the shopper is actively scrolling -- toggled here via a debounced 'scroll'
 * listener that adds/removes an .is-scrolling class. Purely cosmetic; does not affect scroll position,
 * cart state, or any business logic.
 *
 * CROSS-SELL LIMIT (2026-09-16 follow-up): raised from 3 to 10 (Shopify's own max for this endpoint)
 * so products with MORE than 3 configured complementary products actually surface all of them, instead
 * of silently truncating to 3. This required no new scroll logic -- the dialog already has
 * overflow-y:auto + max-height:100% (see CSS) plus the auto-hide scrollbar above, so once cross-sell
 * count exceeds what fits on screen it just scrolls exactly like any other long dialog would. Products
 * with 3 or fewer configured recs behave identically to before (no visual change, no scrolling).
 */

function ftpSuppressCart(on) {
  window.__ftpModalActive = on;
  document.body.toggleAttribute('data-ftp-suppress-cart', on);
}

// Set suppression the instant any add-to-cart form is submitted -- before the fetch even goes out --
// so Kaching Cart's own listener never gets a chance to flash its drawer open first.
document.addEventListener(
  'submit',
  (evt) => {
    const form = evt.target;
    if (form && form.tagName === 'FORM' && /\/cart\/add/.test(form.getAttribute('action') || '')) {
      ftpSuppressCart(true);
    }
  },
  true
);

// Kaching Cart renders its own drawer completely separately from the theme's <cart-drawer> element.
// Wrap its public openCart() so it's a no-op while our modal is active. kachingCartApi loads async, so
// poll briefly until it's available. (The CSS rule in bundle-upsell-modal.css is the real backstop
// since not all of Kaching's internal paths go through this public method.)
function patchKachingCartApi(attempt = 0) {
  const api = window.kachingCartApi;
  if (api && typeof api.openCart === 'function' && !api.__ftpWrapped) {
    const originalOpen = api.openCart.bind(api);
    api.openCart = (...args) => {
      if (window.__ftpModalActive) return undefined;
      return originalOpen(...args);
    };
    api.__ftpWrapped = true;
    return;
  }
  if (attempt < 40) setTimeout(() => patchKachingCartApi(attempt + 1), 100);
}
patchKachingCartApi();

class BundleUpsellModal extends HTMLElement {
  static tierStep(qty) {
    if (qty <= 1) return 1;
    if (qty === 2) return 2;
    return 3;
  }

  constructor() {
    super();
    this.overlay = this.querySelector('.bundle-upsell-modal__overlay');
    this.dialog = this.querySelector('.bundle-upsell-modal__dialog');
    this.closeBtn = this.querySelector('.bundle-upsell-modal__close');
    this.viewCartBtn = this.querySelector('.bundle-upsell-modal__view-cart');
    this.body = this.querySelector('.bundle-upsell-modal__body');
    this.tierContainer = this.querySelector('.bundle-upsell-modal__tier');
    this.qtyContainer = this.querySelector('.bundle-upsell-modal__add-another');

    this.overlay?.addEventListener('click', () => this.hide());
    this.closeBtn?.addEventListener('click', () => this.hide());
    this.viewCartBtn?.addEventListener('click', () => this.goToCart());
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.hide());

    // Auto-hide scrollbar: tint it in only while the shopper is actively scrolling the dialog, then
    // fade it back out ~700ms after they stop. Purely visual -- see CSS for the actual styling.
    this._scrollHideTimer = null;
    this.dialog?.addEventListener(
      'scroll',
      () => {
        this.dialog.classList.add('is-scrolling');
        clearTimeout(this._scrollHideTimer);
        this._scrollHideTimer = setTimeout(() => this.dialog.classList.remove('is-scrolling'), 700);
      },
      { passive: true }
    );

    document.addEventListener('ftp:cart-drawer-rendered', () => this.refreshDrawerBanner());
    document.addEventListener('DOMContentLoaded', () => this.refreshDrawerBanner());
  }

  connectedCallback() {
    // See file header -- escapes a transformed ancestor that broke position:fixed sizing.
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
    }
  }

  cartQuantity(cart) {
    return cart.items.reduce((sum, i) => sum + i.quantity, 0);
  }

  async fetchCart() {
    // cache: 'no-store' is load-bearing -- without it the browser can serve a stale cached snapshot of
    // /cart.js (same URL every call), which was the actual root cause behind a whole family of bugs
    // this build hit: quantities that seemed to jump to old values, and (before units.length became
    // the source of truth for the qty stepper) a stale read feeding wrong math on removal. Any code
    // that needs an accurate "right now" cart state must go through this method.
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' }).then((r) => r.json());
  }

  escape(str) {
    const div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  scaleImage(src, size) {
    if (!src) return '';
    return src.replace(/(\.[a-zA-Z]{2,4})(\?.*)?$/, `_${size}x$1`);
  }

  // Stepper line runs through the dot centers (not as a separate bar above them). Dots are 26px tall,
  // so their vertical center is 13px from the top of the nodes row -- the line divs are positioned to
  // match that exactly. left/right offsets (100/6 %) land on the center of the first/last dot given a
  // 3-column space-between layout.
  buildStepperHTML(step) {
    const steps = [
      { n: '1', label: '1 Item', caption: 'Full price' },
      { n: '2', label: '2 Items', caption: 'Save 10% + Free Shipping' },
      { n: '3+', label: '3+ Items', caption: 'Save 20%' },
    ];
    const fillPct = step === 1 ? 0 : step === 2 ? 50 : 100;
    const edgePct = 100 / 6;
    const spanPct = 100 - edgePct * 2;
    const fillWidthPct = (spanPct * fillPct) / 100;
    const nodesHtml = steps
      .map((s, i) => {
        const idx = i + 1;
        const active = idx <= step;
        const current = idx === step;
        return `
        <div class="ftp-tier-stepper__node${active ? ' is-active' : ''}${current ? ' is-current' : ''}">
          <span class="ftp-tier-stepper__dot">${active ? '✓' : s.n}</span>
          <span class="ftp-tier-stepper__label">${s.label}</span>
          <span class="ftp-tier-stepper__caption">${s.caption}</span>
        </div>`;
      })
      .join('');
    return `
      <div class="ftp-tier-stepper">
        <div class="ftp-tier-stepper__line-bg" style="left:${edgePct}%;right:${edgePct}%"></div>
        <div class="ftp-tier-stepper__line-fill" style="left:${edgePct}%;width:${fillWidthPct}%"></div>
        <div class="ftp-tier-stepper__nodes">${nodesHtml}</div>
      </div>`;
  }

  async refreshDrawerBanner(prefetchedCart) {
    const target = document.getElementById('ftp-bundle-tier-status');
    if (!target) return;
    try {
      const cart = prefetchedCart || (await this.fetchCart());
      const qty = this.cartQuantity(cart);
      if (!qty) {
        target.innerHTML = '';
        return;
      }
      target.innerHTML = `
        <div class="ftp-bundle-tier">
          <p class="ftp-bundle-tier__group-label">Bundle savings</p>
          ${this.buildStepperHTML(BundleUpsellModal.tierStep(qty))}
        </div>`;
    } catch (e) {
      console.error('FTP bundle tier banner refresh failed', e);
    }
  }

  async refreshModalTier(prefetchedCart) {
    if (!this.tierContainer) return;
    try {
      const cart = prefetchedCart || (await this.fetchCart());
      const qty = this.cartQuantity(cart);
      this.tierContainer.innerHTML = this.buildStepperHTML(BundleUpsellModal.tierStep(qty));
    } catch (e) {
      console.error('FTP modal tier refresh failed', e);
    }
  }

  async show(cartResponse) {
    ftpSuppressCart(true);
    this.setAttribute('open', '');
    document.body.classList.add('overflow-hidden');

    const productId = cartResponse.product_id;
    const title = cartResponse.product_title || cartResponse.title;
    const price = ((cartResponse.final_price ?? cartResponse.price) / 100).toFixed(2);
    const image = cartResponse.image;

    // PERF: cross-sell only needs productId (already known synchronously), so kick its fetch off
    // immediately instead of waiting for the cart/quantity work below to finish first. See file header.
    const crossSellPromise = this.renderCrossSell(productId);

    // Establish the TRUE starting quantity for this exact VARIANT (not a product-wide, cross-variant
    // sum) by reading the cart ONCE right after add. This single read is now reused for the tier
    // stepper AND the drawer banner below instead of each independently re-fetching /cart.js (see file
    // header PERF note). This becomes the sole source of truth for the qty stepper from here on (see
    // renderQuantityStepper()) -- no further cart re-fetches for that control. Previously the stepper
    // re-summed ALL cart lines matching this product_id on every render, which silently pulled in
    // unrelated leftover quantity from OTHER variants already sitting in the cart (e.g. from earlier
    // testing/browsing) and fed it into the unit-padding logic -- compounding upward on each "-" click
    // as the inflated count got written back as this variant's absolute quantity. Tracking units.length
    // purely client-side avoids that entirely.
    let baseQty = 1;
    let cart;
    try {
      cart = await this.fetchCart();
      const line = cart.items.find((i) => i.id === cartResponse.id);
      if (line) baseQty = line.quantity;
    } catch (e) {
      /* fall back to 1 */
    }

    this.currentItem = {
      variantId: cartResponse.id,
      productId,
      title,
      handle: cartResponse.handle,
      units: new Array(Math.max(1, baseQty)).fill(cartResponse.id),
    };
    this.productDataCache = null;

    const titleEl = this.body.querySelector('.bundle-upsell-modal__added-title');
    const priceEl = this.body.querySelector('.bundle-upsell-modal__added-price');
    const imgEl = this.body.querySelector('.bundle-upsell-modal__added-image');
    if (titleEl) titleEl.textContent = title;
    if (priceEl) priceEl.textContent = `$${price}`;
    if (imgEl && image) {
      imgEl.src = this.scaleImage(image, 200);
      imgEl.alt = title;
      imgEl.removeAttribute('hidden');
    }

    // PERF: qty stepper (no fetch needed) and both tier displays (reusing the single cart fetch above)
    // now resolve together instead of gating each other in sequence -- this is what used to be 3
    // sequential awaits (renderQuantityStepper, refreshModalTier, refreshDrawerBanner), each a separate
    // visible paint stage.
    await Promise.all([this.renderQuantityStepper(), this.refreshModalTier(cart), this.refreshDrawerBanner(cart)]);
    await crossSellPromise;
  }

  // ---- Quantity stepper for the item just added ---------------------------------------------
  async renderQuantityStepper() {
    if (!this.qtyContainer || !this.currentItem) return;
    // units.length is the sole source of truth for this control now (see show()) -- no cart re-fetch.
    const qty = this.currentItem.units.length;

    this.qtyContainer.innerHTML = `
      <div class="bundle-upsell-modal__qty-row">
        <span class="bundle-upsell-modal__qty-label">Quantity</span>
        <div class="bundle-upsell-modal__qty-stepper">
          <button type="button" class="bundle-upsell-modal__qty-btn" data-action="dec"${qty <= 1 ? ' disabled' : ''} aria-label="Decrease quantity">&minus;</button>
          <span class="bundle-upsell-modal__qty-value">${qty}</span>
          <button type="button" class="bundle-upsell-modal__qty-btn" data-action="inc" aria-label="Increase quantity">+</button>
        </div>
      </div>
      <div class="bundle-upsell-modal__extra-units"></div>`;

    this.qtyContainer.querySelector('[data-action="dec"]')?.addEventListener('click', (e) => this.changeItemQty(-1, e.currentTarget));
    this.qtyContainer.querySelector('[data-action="inc"]')?.addEventListener('click', (e) => this.changeItemQty(1, e.currentTarget));

    await this.renderExtraUnitRows();
  }

  async getProductData() {
    if (this.productDataCache) return this.productDataCache;
    if (!this.currentItem?.handle) return null;
    try {
      this.productDataCache = await fetch(`/products/${this.currentItem.handle}.js`, {
        headers: { Accept: 'application/json' },
      }).then((r) => r.json());
    } catch (e) {
      console.error('FTP product data fetch failed', e);
    }
    return this.productDataCache;
  }

  // Sets the ORIGINAL (first-added) line to an absolute quantity via /cart/change.js, targeted by bare
  // variant id. The real fix for the duplicate-line bug (see changeItemQty's increment branch) was
  // never calling /cart/add.js again for "one more of this variant" -- once there's only ever ONE line
  // for it, targeting by bare variant id is unambiguous and Shopify accepts it fine even though the line
  // carries Kaching Bundles' hidden properties (__kaching_bundles / __kaching_session_id).
  // NOTE: an earlier version of this method targeted the line's exact `key` instead, on the theory that
  // key-based targeting would be more precise. That made things WORSE: confirmed via controlled repro
  // (2026-08-26) that this line's key mutates over time (the properties hash portion changes between
  // requests -- Kaching Bundles appears to rotate __kaching_session_id in the background), so a key
  // captured once at show() time goes stale and /cart/change.js then 400s with "no valid id or line
  // parameter". Bare variant id has no such staleness problem.
  async setOriginalLineQuantity(quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: String(this.currentItem.variantId), quantity }),
    }).then((r) => r.json());
  }

  variantImageSrc(product, variant) {
    return (variant && variant.featured_image && variant.featured_image.src) || product.featured_image || (product.images && product.images[0]) || '';
  }

  // One editable row per unit beyond the first, so a shopper can pick a different color/size for
  // additional units without re-adding from the PDP -- shows the actual product image (updates live
  // when the variant changes) plus a remove button so any specific unit can be taken back out.
  async renderExtraUnitRows() {
    const container = this.qtyContainer?.querySelector('.bundle-upsell-modal__extra-units');
    if (!container) return;
    // units array is authoritative (see show()/renderQuantityStepper()) -- no padding/trimming
    // against a separately-fetched qty needed (that reconciliation loop was the actual root cause of
    // the runaway quantity-inflation bug: it silently duplicated units to match a cart-wide sum that
    // could include unrelated leftover variants, and the inflated count then got written back to the
    // server as this variant's absolute quantity on the next removal).
    const units = this.currentItem.units;

    if (units.length <= 1) {
      container.innerHTML = '';
      return;
    }
    const product = await this.getProductData();
    if (!product || !product.options || product.options.every((o) => o.name === 'Title')) {
      container.innerHTML = '';
      return;
    }

    container.innerHTML = units
      .slice(1)
      .map((variantId, i) => this.extraUnitRowHTML(product, variantId, i + 1))
      .join('');

    units.slice(1).forEach((variantId, i) => this.wireExtraUnitRow(product, i + 1));
  }

  extraUnitRowHTML(product, variantId, unitIndex) {
    const variant = product.variants.find((v) => v.id === variantId) || product.variants[0];
    const img = this.scaleImage(this.variantImageSrc(product, variant), 100);
    const optionsHtml = product.options
      .map((opt, i) => {
        const key = 'option' + (i + 1);
        return `
        <select class="bundle-upsell-modal__variant-select" data-unit="${unitIndex}" data-option-index="${i + 1}" aria-label="${this.escape(opt.name)}">
          ${opt.values
            .map((v) => `<option value="${this.escape(v)}"${v === variant[key] ? ' selected' : ''}>${this.escape(v)}</option>`)
            .join('')}
        </select>`;
      })
      .join('');
    return `
      <div class="bundle-upsell-modal__extra-unit" data-unit="${unitIndex}">
        <img class="bundle-upsell-modal__extra-unit-image" src="${img}" alt="${this.escape(product.title)}" width="44" height="44">
        <div class="bundle-upsell-modal__crosssell-options">${optionsHtml}</div>
        <button type="button" class="bundle-upsell-modal__extra-unit-remove" aria-label="Remove this unit">&times;</button>
      </div>`;
  }

  wireExtraUnitRow(product, unitIndex) {
    const row = this.qtyContainer.querySelector(`.bundle-upsell-modal__extra-unit[data-unit="${unitIndex}"]`);
    if (!row) return;
    const selects = row.querySelectorAll('.bundle-upsell-modal__variant-select');
    const img = row.querySelector('.bundle-upsell-modal__extra-unit-image');
    selects.forEach((s) =>
      s.addEventListener('change', async () => {
        const selected = Array.from(row.querySelectorAll('.bundle-upsell-modal__variant-select')).map((el) => el.value);
        const match = product.variants.find((v) => selected.every((val, i) => v['option' + (i + 1)] === val));
        if (match && match.available) {
          if (img) img.src = this.scaleImage(this.variantImageSrc(product, match), 100);
          await this.swapUnitVariant(unitIndex, match.id);
        }
      })
    );
    row.querySelector('.bundle-upsell-modal__extra-unit-remove')?.addEventListener('click', () => this.removeUnit(unitIndex));
  }

  async swapUnitVariant(unitIndex, newVariantId) {
    const oldVariantId = this.currentItem.units[unitIndex];
    if (oldVariantId === newVariantId) return;
    try {
      // Compute the OLD variant's target quantity from our own client-tracked units array (how many
      // units still reference it after this swap) instead of re-fetching the cart and subtracting 1.
      // Re-reading the cart here was race/cache-prone -- a stale GET could return a pre-increment
      // quantity, causing the wrong absolute value to be written back (e.g. 2 units collapsing to 0
      // after a single change). Our units array is the authoritative source of truth either way.
      this.currentItem.units[unitIndex] = newVariantId;
      const remainingOldCount = this.currentItem.units.filter((v) => v === oldVariantId).length;
      // Route through the helper when the old variant is the original line (see setOriginalLineQuantity())
      // for consistency. /cart/change.js also validates `id` against a string-only regex -- a raw JSON
      // number fails with a cryptic "expected Integer to be a ... : id" 400, even though the value is
      // numeric.
      if (oldVariantId === this.currentItem.variantId) {
        await this.setOriginalLineQuantity(remainingOldCount);
      } else {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: String(oldVariantId), quantity: remainingOldCount }),
        });
      }
      await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: newVariantId, quantity: 1 }),
      });
      await this.silentDrawerRefresh();
      await this.refreshModalTier();
      await this.refreshDrawerBanner();
    } catch (e) {
      console.error('FTP unit variant swap failed', e);
    }
  }

  // Explicit per-row removal -- decouples "remove THIS unit" from the top-level qty stepper's "-",
  // which only ever removed the last unit and made it look like extra units couldn't be taken back out.
  async removeUnit(unitIndex) {
    const variantId = this.currentItem.units[unitIndex];
    if (!variantId) return;
    try {
      // Same fix as swapUnitVariant(): splice first, then tell the server the exact absolute quantity
      // that should remain for this variant based on our own units array, rather than reading the
      // cart's current line quantity and subtracting 1 (which could race/read stale and, e.g., send a
      // 2-unit line straight to 0 on a single removal instead of down to 1).
      this.currentItem.units.splice(unitIndex, 1);
      const remainingForVariant = this.currentItem.units.filter((v) => v === variantId).length;
      // Route through the helper for the original variant -- see setOriginalLineQuantity(). Same
      // string-only `id` requirement as swapUnitVariant() applies to the fallback path -- a raw number
      // 400s.
      if (variantId === this.currentItem.variantId) {
        await this.setOriginalLineQuantity(remainingForVariant);
      } else {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: String(variantId), quantity: remainingForVariant }),
        });
      }
      await this.silentDrawerRefresh();
      await this.renderQuantityStepper();
      await this.refreshModalTier();
      await this.refreshDrawerBanner();
      // NOTE: do NOT call window.kachingCartApi.refreshCart() here. Confirmed via controlled repro
      // (2026-08-26) that calling it right after a /cart/change.js update does not just re-sync Kaching's
      // drawer -- it creates a SECOND, duplicate cart line for the same variant (two lines with the same
      // id, quantity 1 each, instead of merging), which is exactly what caused the tier bar to appear
      // "stuck" one tier too high after a quantity decrease. Kaching's drawer still gets fresh data the
      // normal way when the shopper actually opens it via goToCart() -> kachingCartApi.openCart().
    } catch (e) {
      console.error('FTP remove unit failed', e);
    }
  }

  async changeItemQty(delta, btn) {
    if (!this.currentItem) return;
    btn.disabled = true;
    try {
      if (delta > 0) {
        const variantId = this.currentItem.units[this.currentItem.units.length - 1] || this.currentItem.variantId;
        // If we're adding one more of the ORIGINAL variant, target that exact line via /cart/change.js
        // (absolute quantity) instead of /cart/add.js -- see setOriginalLineQuantity(). Using add.js here
        // creates a second, properties-less line instead of incrementing the Kaching-decorated one
        // (confirmed via controlled repro, 2026-08-26), which is what made the tier bar/qty look "stuck"
        // after a subsequent decrement targeted the wrong line.
        let response;
        if (variantId === this.currentItem.variantId) {
          response = await this.setOriginalLineQuantity(this.currentItem.units.length + 1);
          if (response.status) throw new Error(response.description || 'Add failed');
        } else {
          response = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 }),
          }).then((r) => r.json());
          if (response.status) throw new Error(response.description || 'Add failed');
        }
        this.currentItem.units.push(variantId);
        this.publishCartUpdate(variantId, response);
        await this.silentDrawerRefresh();
        await this.renderQuantityStepper();
      } else {
        await this.removeUnit(this.currentItem.units.length - 1);
      }
      await this.refreshModalTier();
      await this.refreshDrawerBanner();
      // See the note in removeUnit() -- window.kachingCartApi.refreshCart() creates a duplicate cart
      // line instead of syncing, so it's intentionally not called here either.
    } catch (e) {
      console.error('FTP quantity change failed', e);
    } finally {
      btn.disabled = false;
    }
  }

  publishCartUpdate(variantId, cartData) {
    if (window.PUB_SUB_EVENTS && typeof publish === 'function') {
      publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-upsell-modal', productVariantId: variantId, cartData });
    }
  }

  async silentDrawerRefresh() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const sectionsRes = await fetch(`${root}?sections=cart-drawer,cart-icon-bubble`, {
      headers: { Accept: 'application/json' },
    }).then((r) => r.json());
    cartDrawer.renderContents({ id: Date.now(), sections: sectionsRes }, { skipOpen: true });
  }

  // ---- Cross-sell ("STACK AND SAVE") ---------------------------------------------------------
  async quickAdd(variantId, btn, opts = {}) {
    const idleLabel = opts.idleLabel ?? 'Add';
    btn.disabled = true;
    btn.textContent = 'Adding…';
    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      }).then((r) => r.json());
      if (response.status) throw new Error(response.description || response.message || 'Add to cart failed');

      this.publishCartUpdate(variantId, response);
      await this.silentDrawerRefresh();
      // See the note in removeUnit() -- window.kachingCartApi.refreshCart() creates a duplicate cart
      // line instead of syncing, so it's intentionally not called here either.
      await this.refreshModalTier();
      await this.refreshDrawerBanner();

      btn.disabled = false;
      btn.textContent = idleLabel;
      if (opts.onSuccess) await opts.onSuccess();
      return true;
    } catch (e) {
      console.error('FTP quick add failed', e);
      btn.disabled = false;
      btn.textContent = idleLabel;
      return false;
    }
  }

  async renderCrossSell(productId) {
    const container = this.querySelector('.bundle-upsell-modal__crosssell-list');
    const section = this.querySelector('.bundle-upsell-modal__crosssell');
    if (!container) return;
    container.innerHTML = '<p class="bundle-upsell-modal__loading">Loading suggestions…</p>';
    try {
      // limit=10 is Shopify's own max for this endpoint (2026-09-16 follow-up, raised from 3) -- lets
      // products with more than 3 configured complementary recs show all of them. The dialog already
      // scrolls (overflow-y:auto + max-height:100%, see CSS) once content exceeds the viewport, so
      // nothing else needs to change for this to work; products with 3 or fewer recs render exactly as
      // before.
      const res = await fetch(`/recommendations/products.json?product_id=${productId}&intent=complementary&limit=10`, {
        headers: { Accept: 'application/json' },
      });
      const data = await res.json();
      this.crossSellProducts = (data.products || []).filter((p) => p.available);
      if (!this.crossSellProducts.length) {
        container.innerHTML = '';
        if (section) section.setAttribute('hidden', '');
        return;
      }
      if (section) section.removeAttribute('hidden');

      container.innerHTML = this.crossSellProducts.map((p, idx) => this.crossSellCardHTML(p, idx)).join('');
      this.crossSellProducts.forEach((p, idx) => this.wireCrossSellCard(p, idx));
    } catch (e) {
      container.innerHTML = '';
      if (section) section.setAttribute('hidden', '');
      console.error('FTP cross-sell fetch failed', e);
    }
  }

  // Compact aug11-style layout: small image on the left, everything else (title, variant selects,
  // price, qty note, Add button) stacked in a column to the right.
  crossSellCardHTML(p, idx) {
    const firstAvailable = p.variants.find((v) => v.available) || p.variants[0];
    const optionsHtml = (p.options || [])
      .map((opt, i) => {
        const key = 'option' + (i + 1);
        return `
        <select class="bundle-upsell-modal__variant-select" data-idx="${idx}" data-option-index="${i + 1}" aria-label="${this.escape(opt.name)}">
          ${opt.values
            .map((v) => `<option value="${this.escape(v)}"${v === firstAvailable[key] ? ' selected' : ''}>${this.escape(v)}</option>`)
            .join('')}
        </select>`;
      })
      .join('');
    const img = this.scaleImage(p.featured_image, 150);
    const productUrl = `/products/${p.handle}`;

    return `
      <div class="bundle-upsell-modal__crosssell-item" data-idx="${idx}" data-variant-id="${firstAvailable.id}">
        <a class="bundle-upsell-modal__crosssell-image-link" href="${productUrl}" target="_blank" rel="noopener">
          <img class="bundle-upsell-modal__crosssell-image" src="${img}" alt="${this.escape(p.title)}" loading="lazy" width="80" height="80">
        </a>
        <div class="bundle-upsell-modal__crosssell-info">
          <a class="bundle-upsell-modal__crosssell-link" href="${productUrl}" target="_blank" rel="noopener">
            <p class="bundle-upsell-modal__crosssell-title">${this.escape(p.title)}</p>
          </a>
          ${optionsHtml ? `<div class="bundle-upsell-modal__crosssell-options">${optionsHtml}</div>` : ''}
          <p class="bundle-upsell-modal__crosssell-price">$${(firstAvailable.price / 100).toFixed(2)}</p>
          <p class="bundle-upsell-modal__crosssell-qty" hidden></p>
          <button type="button" class="bundle-upsell-modal__crosssell-add button button--secondary"${firstAvailable.available ? '' : ' disabled'}>
            ${firstAvailable.available ? 'Add' : 'Sold out'}
          </button>
        </div>
      </div>`;
  }

  wireCrossSellCard(p, idx) {
    const card = this.querySelector(`.bundle-upsell-modal__crosssell-item[data-idx="${idx}"]`);
    if (!card) return;
    const selects = card.querySelectorAll('.bundle-upsell-modal__variant-select');
    const addBtn = card.querySelector('.bundle-upsell-modal__crosssell-add');
    const priceEl = card.querySelector('.bundle-upsell-modal__crosssell-price');
    const qtyEl = card.querySelector('.bundle-upsell-modal__crosssell-qty');
    const imgEl = card.querySelector('.bundle-upsell-modal__crosssell-image');

    const recompute = () => {
      const selected = Array.from(selects).map((s) => s.value);
      const match = p.variants.find((v) => selected.every((val, i) => v['option' + (i + 1)] === val));
      if (match) {
        card.dataset.variantId = match.id;
        if (priceEl) priceEl.textContent = `$${(match.price / 100).toFixed(2)}`;
        // Same live-image-swap pattern used for the qty-stepper's extra unit rows -- show the actual
        // selected variant's photo instead of leaving the first-available-variant image stuck in place.
        if (imgEl) imgEl.src = this.scaleImage(this.variantImageSrc(p, match), 150);
        addBtn.disabled = !match.available;
        addBtn.textContent = match.available ? 'Add' : 'Sold out';
        this.refreshCrossSellQty(card, match.id, qtyEl);
      } else {
        addBtn.disabled = true;
        addBtn.textContent = 'Unavailable';
      }
    };
    selects.forEach((s) => s.addEventListener('change', recompute));

    addBtn.addEventListener('click', async () => {
      const variantId = card.dataset.variantId;
      await this.quickAdd(variantId, addBtn, {
        idleLabel: 'Add',
        onSuccess: () => this.refreshCrossSellQty(card, variantId, qtyEl),
      });
    });

    this.refreshCrossSellQty(card, card.dataset.variantId, qtyEl);
  }

  async refreshCrossSellQty(card, variantId, qtyEl) {
    if (!qtyEl) return;
    try {
      const cart = await this.fetchCart();
      const line = cart.items.find((i) => i.id === Number(variantId));
      if (line && line.quantity > 0) {
        qtyEl.textContent = `In cart: ${line.quantity}`;
        qtyEl.hidden = false;
      } else {
        qtyEl.hidden = true;
      }
    } catch (e) {
      /* noop */
    }
  }

  goToCart() {
    this.hide();
    ftpSuppressCart(false);
    if (window.kachingCartApi && typeof window.kachingCartApi.openCart === 'function') {
      window.kachingCartApi.openCart();
      return;
    }
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.open();
    } else {
      window.location.href = window.routes.cart_url;
    }
  }

  hide() {
    this.removeAttribute('open');
    document.body.classList.remove('overflow-hidden');
    ftpSuppressCart(false);
  }
}

if (!customElements.get('bundle-upsell-modal')) {
  customElements.define('bundle-upsell-modal', BundleUpsellModal);
}
